// Members of the selected logical group. Members reference areas by id;
// we resolve labels via the shared site-areas map and offer an
// autocomplete of areas not yet in the group.

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { logicalGroups } from "../../api/endpoints/logicalGroups";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { AutocompleteField } from "../../components/forms/AutocompleteField";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Table } from "../../components/ui/Table";
import type { LogicalGroupMemberOut } from "../../api/types/logicalGroup";

import type { SiteArea, SiteAreas } from "../areas/useSiteAreas";

interface Props {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
  groupSlug: string;
  areas: SiteAreas;
}

export function GroupMembersPanel({
  accountSlug,
  tenantSlug,
  siteSlug,
  groupSlug,
  areas,
}: Props) {
  const queryClient = useQueryClient();

  const membersQ = useQuery({
    ...adminQueryOptions,
    queryKey: qk.logicalGroupMembers(accountSlug, tenantSlug, siteSlug, groupSlug),
    queryFn: () =>
      logicalGroups
        .listMembers(accountSlug, tenantSlug, siteSlug, groupSlug)
        .then((r) => r.data),
  });

  const add = useMutation({
    mutationFn: (areaId: string) =>
      logicalGroups.createMember(accountSlug, tenantSlug, siteSlug, groupSlug, {
        area_id: areaId,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: qk.logicalGroupMembers(accountSlug, tenantSlug, siteSlug, groupSlug),
      }),
  });

  const members = membersQ.data ?? [];
  // Block adds until current membership is known (and while a post-add
  // refetch is in flight) so the operator can't add against a stale list
  // or double-add the same area before the invalidation settles.
  const membersBusy = membersQ.isPending || membersQ.isFetching;
  const memberAreaIds = useMemo(
    () => new Set(members.map((m) => m.area_id)),
    [members],
  );
  // Areas not already in the group.
  const available = useMemo(
    () => areas.items.filter((a) => !memberAreaIds.has(a.id)),
    [areas.items, memberAreaIds],
  );

  function labelFor(areaId: string): string {
    const a = areas.byId.get(areaId);
    return a ? `${a.label} · ${a.floorLabel}` : areaId;
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
      <h4 style={{ margin: "0 0 8px", fontSize: "0.9rem" }}>
        Members of <code>{groupSlug}</code>
      </h4>

      {(areas.error || membersQ.error) && (
        <ErrorBanner
          error={areas.error || membersQ.error}
          title="Failed to load members"
        />
      )}
      {add.error != null && (
        <div style={{ marginBottom: 8 }}>
          <ErrorBanner error={add.error} title="Failed to add member" />
        </div>
      )}

      <div style={{ maxWidth: 420, marginBottom: 12 }}>
        <AutocompleteField<SiteArea>
          label="Add area to group"
          items={available}
          value={null}
          onSelect={(a) => {
            if (a) add.mutate(a.id);
          }}
          getKey={(a) => a.id}
          getLabel={(a) => `${a.label} · ${a.floorLabel}`}
          getSecondary={(a) => a.slug}
          disabled={areas.loading || membersBusy || add.isPending}
          placeholder={
            areas.loading
              ? "Loading areas…"
              : membersBusy
                ? "Syncing members…"
                : available.length === 0
                  ? "All areas already in this group"
                  : "Pick an area to add"
          }
          helper="Same-site areas only; backend rejects cross-site members."
          allowClear
        />
      </div>

      {membersQ.isPending ? (
        <p style={{ color: "var(--text-dim)" }}>Loading members…</p>
      ) : (
        <Table<LogicalGroupMemberOut>
          columns={[
            { key: "area", header: "Area", render: (m) => labelFor(m.area_id) },
            {
              key: "area_id",
              header: "Area id",
              render: (m) => (
                <code style={{ fontSize: "0.76rem", color: "var(--text-dim)" }}>
                  {m.area_id}
                </code>
              ),
            },
          ]}
          rows={members}
          rowKey={(m) => m.id}
          empty={
            <EmptyState
              title="No members yet"
              description="Add the first area to this group above."
            />
          }
        />
      )}
    </div>
  );
}
